// Zilvix priced everything in a hardcoded '$' before the store's own currency was read
// from GET /api/settings/public, so USD stays the fallback when a store has none set.
const DEFAULT_CODE = 'USD';
const DEFAULT_LOCALE = 'en-US';

/**
 * Reads currency from GET /api/settings/public (Quazbit dashboard).
 * Supports: currency, currencyCode, currencySymbol (and snake_case variants).
 */
export function parseCurrencyFromSettings(settings) {
    if (!settings || typeof settings !== 'object') {
        return {
            code: DEFAULT_CODE,
            symbol: symbolFromCurrencyCode(DEFAULT_CODE, DEFAULT_LOCALE),
            locale: DEFAULT_LOCALE,
        };
    }

    const rawCode = (
        settings.currencyCode ??
        settings.currency_code ??
        settings.currency ??
        DEFAULT_CODE
    )
        .toString()
        .trim()
        .toUpperCase();

    const explicitSymbol = (settings.currencySymbol ?? settings.currency_symbol ?? '')
        .toString()
        .trim();

    const validCode = /^[A-Z]{3}$/.test(rawCode) ? rawCode : DEFAULT_CODE;

    const locale =
        (settings.locale ?? settings.locale_code ?? DEFAULT_LOCALE).toString().trim() || DEFAULT_LOCALE;

    let symbol = explicitSymbol;
    if (!symbol) {
        symbol = symbolFromCurrencyCode(validCode, locale);
    }

    return { code: validCode, symbol, locale };
}

function symbolFromCurrencyCode(code, locale = DEFAULT_LOCALE) {
    try {
        const parts = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(0);
        const cur = parts.find((p) => p.type === 'currency');
        return cur?.value || '¤';
    } catch {
        return '¤';
    }
}

// Currencies with 3 minor digits (Gulf dinars, Omani rial); everything else keeps 2.
const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

/** Decimals shown and charged for `currencyCode`: 1.125 KWD, 12.50 SAR. */
export function currencyFractionDigits(currencyCode) {
    return THREE_DECIMAL_CURRENCIES.has(String(currencyCode || '').toUpperCase()) ? 3 : 2;
}

/** Rounds like the server does before charging. */
export function roundMoney(amount, currencyCode) {
    const factor = 10 ** currencyFractionDigits(currencyCode);
    return Math.round((Number(amount) || 0) * factor) / factor;
}

export function formatMoney(amount, currencyCode, options = {}) {
    const n = Number(amount);
    if (Number.isNaN(n)) return '';
    const code = currencyCode || DEFAULT_CODE;
    const locale = options.locale || DEFAULT_LOCALE;
    const digits = currencyFractionDigits(code);
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(n);
    } catch {
        const sym = options.fallbackSymbol ?? '¤';
        return `${sym}${n.toFixed(digits)}`;
    }
}

/**
 * Order/track APIs may return currency as an ISO code (USD) or a display symbol ($).
 */
export function formatOrderMoney(amount, currencyRaw, fallbackCode, options = {}) {
    const n = Number(amount);
    if (Number.isNaN(n)) return '';
    const raw = (currencyRaw ?? '').toString().trim();
    if (/^[A-Z]{3}$/i.test(raw)) {
        return formatMoney(n, raw.toUpperCase(), options);
    }
    if (raw) {
        return `${n.toFixed(currencyFractionDigits(fallbackCode))} ${raw}`;
    }
    return formatMoney(n, fallbackCode || DEFAULT_CODE, options);
}

// --- Active store currency -------------------------------------------------
// Module state, kept in sync by PublicSettingsProvider. React components should
// read formatPrice from usePublicSettings() so they re-render when it changes;
// this exists for the pure helpers (productPricing, orderDisplay) that format
// money outside a component and cannot call a hook.

let activeCurrency = {
    code: DEFAULT_CODE,
    symbol: symbolFromCurrencyCode(DEFAULT_CODE, DEFAULT_LOCALE),
    locale: DEFAULT_LOCALE,
};

export function setActiveCurrency(currency) {
    if (!currency?.code) return;
    activeCurrency = currency;
}

export function getActiveCurrency() {
    return activeCurrency;
}

/** Format using whatever currency the active store last reported. */
export function formatActiveMoney(amount) {
    return formatMoney(amount, activeCurrency.code, {
        fallbackSymbol: activeCurrency.symbol,
        locale: activeCurrency.locale,
    });
}
